import { type CallWindow } from '@kalos/kalos-rpc';
import { Button, cn } from '@kalos/ui';
import { Lock } from 'lucide-react';
import { type FC, Fragment } from 'react';

import { formatTimeCondensed } from '../../../../tools/formatTimeUtils';

export interface JobWindowCardProps {
  window: CallWindow;
  jobsBooked: number;
  techsAssigned: number;
  techsOnStandby: number;
  techsFinishingSoon: number;
  /** Per-job-type counts for this window's selected department, sorted highest to lowest */
  jobTypeBreakdown?: { jobType: string; count: number }[];
  isRecommended: boolean;
  confidence?: 'high' | 'medium' | 'low';
  arrivalEstimate?: string;
  isPast: boolean;
  isSelected: boolean;
  isBlocked?: boolean;
  /** Overall max-jobs limit reached (soft) — still selectable as an overbook */
  atCapacity?: boolean;
  /** Effective max jobs for this window, shown in the overbook warning */
  capacityMax?: number;
  /** Maintenance window-limit reached for a maintenance booking (hard) — unselectable */
  maintLimitReached?: boolean;
  /**
   * The day's cap for the job type being booked is met (hard) — unselectable. Unlike
   * maintLimitReached this is the same for every window on the day, so when it is set the whole
   * grid is disabled and CallWindowAvailability explains why above it.
   */
  dailyCapReached?: boolean;
  onSelect: () => void;
}

export const JobWindowCard: FC<JobWindowCardProps> = ({
  window,
  jobsBooked,
  techsAssigned,
  techsOnStandby,
  techsFinishingSoon,
  jobTypeBreakdown = [],
  isRecommended,
  confidence = 'medium',
  arrivalEstimate,
  isPast,
  isSelected,
  isBlocked = false,
  atCapacity = false,
  capacityMax = 0,
  maintLimitReached = false,
  dailyCapReached = false,
  onSelect,
}) => {
  const startTime = formatTimeCondensed(window.startTime);
  const endTime = formatTimeCondensed(window.endTime);
  const timeRange = `${startTime}-${endTime}`;

  const availableTechs = techsOnStandby + techsFinishingSoon;
  // Maintenance limit and the daily job-type cap are both hard blocks — the window cannot be
  // selected. They can fire independently: one is per-window, the other per-day.
  const isDisabled = isPast || maintLimitReached || dailyCapReached;

  return (
    <div
      className={cn(
        // Full-height flex column so the Select button can sit flush at the bottom
        // regardless of how much content each card renders above it.
        'relative flex h-full flex-col rounded-lg border-2 p-3 transition-all',
        isDisabled
          ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-50'
          : isSelected && isBlocked
            ? 'border-red-500 bg-red-50 shadow-md ring-2 ring-red-200'
            : isSelected
              ? 'border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-200'
              : isBlocked
                ? 'cursor-pointer border-red-200 bg-red-50 opacity-80 hover:border-red-400'
                : atCapacity
                  ? 'cursor-pointer border-amber-500 bg-[#fefde9] hover:border-amber-600'
                  : isRecommended
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 bg-white hover:border-gray-400',
      )}
    >
      {/* Time Window */}
      <div className="mb-2">
        <h3 className={cn('text-lg font-semibold', isPast ? 'text-gray-400' : 'text-gray-900')}>
          {timeRange}
        </h3>
      </div>

      {/* Jobs headline */}
      <div className="mb-1">
        <span className={cn('text-sm font-bold', isPast ? 'text-gray-400' : 'text-gray-900')}>
          Jobs {jobsBooked}
        </span>
      </div>

      {/* Job-type breakdown - muted, non-zero types only, sorted highest to lowest */}
      {jobTypeBreakdown.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-500">
          {jobTypeBreakdown.map((item, idx) => (
            <Fragment key={item.jobType}>
              {idx > 0 && <span className="text-gray-300">·</span>}
              <span className="whitespace-nowrap">
                {item.jobType} {item.count}
              </span>
            </Fragment>
          ))}
        </div>
      )}

      {/* Techs headline */}
      <div className="mb-1">
        <span className={cn('text-sm font-bold', isPast ? 'text-gray-400' : 'text-gray-900')}>
          Techs {techsAssigned}
        </span>
      </div>

      {/* Tech Availability Status - Compact horizontal layout */}
      {!isPast && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {techsOnStandby > 0 && (
            <div className="flex items-center gap-1 whitespace-nowrap">
              <span className="size-2 rounded-full bg-green-500" />
              <span className="text-gray-700">{techsOnStandby} standby</span>
            </div>
          )}
          {techsFinishingSoon > 0 && (
            <div className="flex items-center gap-1 whitespace-nowrap">
              <span className="size-2 rounded-full bg-orange-500" />
              <span className="text-gray-700">{techsFinishingSoon} finishing</span>
            </div>
          )}
          {availableTechs === 0 && (
            <div className="flex items-center gap-1 whitespace-nowrap">
              <span className="size-2 rounded-full bg-red-500" />
              <span className="text-gray-700">No techs</span>
            </div>
          )}
        </div>
      )}

      {/* Arrival Estimate */}
      {!isPast && arrivalEstimate && (
        <div className="mb-2 text-xs font-medium text-green-600">{arrivalEstimate}</div>
      )}

      {/* Limit warnings */}
      {!isPast && maintLimitReached && (
        <div className="mb-2 text-xs font-medium leading-tight text-red-600">
          Maintenance limit reached for this window.
        </div>
      )}
      {!isPast && !maintLimitReached && dailyCapReached && (
        <div className="mb-2 text-xs font-medium leading-tight text-red-600">
          Daily cap reached for this job type.
        </div>
      )}
      {!isPast && !maintLimitReached && !dailyCapReached && atCapacity && (
        <div className="mb-2 text-xs font-medium leading-tight text-amber-600">
          At capacity — {jobsBooked} of {capacityMax} jobs. Selecting will overbook this window.
        </div>
      )}

      {/* Select Button */}
      <Button
        type="button"
        onClick={onSelect}
        disabled={isDisabled}
        variant={isSelected ? 'default' : 'outline'}
        size="sm"
        className={cn(
          'mt-auto w-full',
          isSelected && !isBlocked && 'bg-blue-600 text-white hover:bg-blue-700',
          isSelected && isBlocked && 'bg-red-600 text-white hover:bg-red-700',
          // Overbook (soft cap) uses amber to distinguish from hard blocks.
          !isSelected &&
            atCapacity &&
            !isBlocked &&
            !isDisabled &&
            'border-amber-500 bg-amber-500 text-white hover:bg-amber-600',
          isDisabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {maintLimitReached ? (
          <span className="flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Maint limit reached
          </span>
        ) : dailyCapReached ? (
          <span className="flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Daily cap reached
          </span>
        ) : isSelected && isBlocked ? (
          <span className="flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Selected (Blocked)
          </span>
        ) : isBlocked ? (
          <span className="flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Blocked
          </span>
        ) : isSelected ? (
          'Selected'
        ) : atCapacity ? (
          'Select (Overbook)'
        ) : (
          'Select'
        )}
      </Button>
    </div>
  );
};
